import { defineComponent, FunctionalComponent, h } from 'vue'
import { Translation } from 'vue-i18n'

/**
 * `<i18n-t>`, rendering a `<span>` unless it is given a `tag`.
 *
 * Vue 2's `<i18n>` wrapped its output in a span by default, and every template here was written
 * against that. vue-i18n 9's `Translation` renders a bare fragment instead, so its text and slotted
 * elements land directly in the parent: in a flex container each word run and `<strong>` became its
 * own flex item ("with / name / on / title" in columns on the messages list, "Reviewed on" above its
 * date), and an attribute such as `class="sr-only"` had no element to sit on and was dropped.
 *
 * It also puts numbered slots back to work. Vue 2's `<i18n>` filled `{0}` from `<template #0>`;
 * vue-i18n 9 hands every named slot over as a named value, which a `{0}` never reads, so it rendered
 * nothing ("{0} related {1}" in the delete dialog came out as just "related"). A list only reaches
 * `{0}` through the default slot, so numbered slots are passed as that, in order.
 *
 * And it reads the global scope. Translation defaults to `scope="parent"`, but in legacy mode no
 * component here has a translator of its own, so the lookup always walked to the root, fell back to
 * the global one and warned "Not found parent scope" on every render.
 *
 * Registered as `i18n-t` in place of vue-i18n's own (see `configure` in boot/i18n.ts), so every
 * existing `<i18n-t>` gets it. An explicit `tag` or `scope` still wins.
 */
const isNumbered = (names: string[]) => names.length > 0 && names.every(name => /^\d+$/.test(name))

// Renders its slot as it is. Vue turns a bare array into a Fragment, and Translation spreads those.
const Nodes: FunctionalComponent = (_props, { slots }) => slots.default!()

export default defineComponent({
  name: 'I18nT',

  // Everything - keypath, plural, tag, class, data-* - goes to Translation as given.
  inheritAttrs: false,

  setup (_props, { attrs, slots }) {
    const asList = () => ({
      // One entry per placeholder, however many nodes its slot renders.
      default: () => Object.keys(slots)
        .sort((a, b) => Number(a) - Number(b))
        .map(name => {
          const nodes = slots[name]!()
          return nodes.length === 1 ? nodes[0] : h(Nodes, null, { default: () => nodes })
        })
    })

    return () => h(Translation as never, { tag: 'span', scope: 'global', ...attrs }, isNumbered(Object.keys(slots)) ? asList() : slots)
  }
})
